import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Space, Row, Col,
    message, Select, Typography, Avatar, Empty, Tabs
} from 'antd';
import {
    ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
    UserOutlined, WalletOutlined, TeamOutlined,
    CrownOutlined, ReloadOutlined, EyeOutlined,
    DollarOutlined, FilterOutlined, AppstoreOutlined, SyncOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const CommissionsPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [migrationLoading, setMigrationLoading] = useState(false);
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [stats, setStats] = useState({
        totalAffiliates: 0,
        totalAmbassadors: 0,
        totalPendingAffiliate: 0,
        totalPendingAmbassador: 0,
        pendingAffiliate: 0,
        pendingAmbassador: 0,
        totalPending: 0
    });

    const fetchCommissions = useCallback(async () => {
        setLoading(true);
        try {
            // Déterminer le recipientType selon l'onglet actif
            let recipientType = '';

            if (activeTab === 'affiliate' || activeTab === 'ambassador') {
                recipientType = activeTab;
            }
            // Pour hours130, on récupère tous les bénéficiaires (pas de filtre recipientType)

            const params = {
                grouped: 'true',
                recipientType
            };
            Object.keys(params).forEach(key => !params[key] && delete params[key]);

            const response = await affiliatesService.getCommissions(params);
            if (response.success) {
                setBeneficiaries(response.data || []);
                if (response.stats) {
                    setStats({
                        totalAffiliates: response.stats.totalAffiliates || 0,
                        totalAmbassadors: response.stats.totalAmbassadors || 0,
                        totalPendingAffiliate: response.stats.totalPendingAffiliate || 0,
                        totalPendingAmbassador: response.stats.totalPendingAmbassador || 0,
                        pendingAffiliate: response.stats.pendingAffiliate || 0,
                        pendingAmbassador: response.stats.pendingAmbassador || 0,
                        totalPending: (response.stats.totalPendingAffiliate || 0) + (response.stats.totalPendingAmbassador || 0)
                    });
                }
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => { fetchCommissions(); }, [fetchCommissions]);

    const handleTabChange = (key) => {
        setActiveTab(key);
    };

    const handleSyncAmbassadorCommissions = async () => {
        setSyncLoading(true);
        try {
            const response = await affiliatesService.generateAmbassadorCommissions();
            if (response.success) {
                message.success(response.message || 'Synchronisation terminée');
                // Rafraîchir les données
                fetchCommissions();
            } else {
                message.error(response.message || 'Erreur lors de la synchronisation');
            }
        } catch (error) {
            message.error('Erreur lors de la synchronisation des commissions');
        } finally {
            setSyncLoading(false);
        }
    };

    const handleMigration = async () => {
        setMigrationLoading(true);
        try {
            const response = await affiliatesService.generateMissingCommissions();
            if (response.success) {
                message.success(response.message || 'Migration terminée');
                fetchCommissions();
            } else {
                message.error(response.message || 'Erreur lors de la migration');
            }
        } catch (error) {
            message.error('Erreur lors de la migration des commissions');
        } finally {
            setMigrationLoading(false);
        }
    };

    // Filtrer localement par onglet
    const getFilteredBeneficiaries = () => {
        if (activeTab === 'all') return beneficiaries;
        return beneficiaries.filter(b => b.recipientType === activeTab);
    };

    const columns = [
        {
            title: 'Bénéficiaire',
            key: 'beneficiary',
            render: (_, record) => {
                const isAmbassador = record.recipientType === 'ambassador';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar
                            size={44}
                            style={{ background: isAmbassador ? '#722ed1' : '#1890ff', flexShrink: 0 }}
                            icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                        />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: '#262626', fontSize: 15 }}>
                                {record.firstName} {record.lastName}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.phone}</div>
                            <Tag
                                color={isAmbassador ? 'purple' : 'blue'}
                                style={{ marginTop: 4, borderRadius: 10, fontSize: 11 }}
                            >
                                {isAmbassador ? 'Ambassadeur' : 'Affilié'}
                            </Tag>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Commissions',
            key: 'commissions',
            width: 180,
            render: (_, record) => (
                <div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {record.pendingCount > 0 && (
                            <Tag color="gold" style={{ borderRadius: 10 }}>
                                <ClockCircleOutlined /> {record.pendingCount} en attente
                            </Tag>
                        )}
                        {record.approvedCount > 0 && (
                            <Tag color="blue" style={{ borderRadius: 10 }}>
                                <CheckCircleOutlined /> {record.approvedCount} approuvée{record.approvedCount > 1 ? 's' : ''}
                            </Tag>
                        )}
                        {record.paidCount > 0 && (
                            <Tag color="green" style={{ borderRadius: 10 }}>
                                <CheckCircleOutlined /> {record.paidCount} payée{record.paidCount > 1 ? 's' : ''}
                            </Tag>
                        )}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                        Total: {record.commissionsCount} commission{record.commissionsCount > 1 ? 's' : ''}
                    </div>
                </div>
            )
        },
        {
            title: 'Montant',
            key: 'amount',
            width: 150,
            render: (_, record) => (
                <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}>
                        {record.totalAmount.toLocaleString()} <span style={{ fontSize: 12 }}>F</span>
                    </div>
                    {record.pendingAmount > 0 && (
                        <div style={{ fontSize: 12, color: '#faad14' }}>
                            {record.pendingAmount.toLocaleString()} F en attente
                        </div>
                    )}
                </div>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => {
                        // Naviguer vers la page de détail de commission avec le type et l'ID du bénéficiaire
                        if (record.lastCommissionId) {
                            navigate(`/dashboard/affiliate-commissions/${record.lastCommissionId}?type=${record.recipientType}`);
                        } else {
                            // Naviguer vers la page des commissions du bénéficiaire
                            navigate(`/dashboard/affiliate-commissions/beneficiary/${record.recipientType}/${record._id}`);
                        }
                    }}
                    style={{ color: '#1890ff' }}
                />
            )
        }
    ];

    // Stats Card Component
    const StatCard = ({ icon, iconBg, title, value, suffix, subtitle, onClick }) => (
        <Card
            style={{
                borderRadius: 12,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s',
                height: '100%'
            }}
            bodyStyle={{ padding: 16 }}
            hoverable={!!onClick}
            onClick={onClick}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    {icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{title}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#262626', lineHeight: 1.2 }}>
                        {typeof value === 'number' ? value.toLocaleString() : value}
                        {suffix && <span style={{ fontSize: 14, fontWeight: 500, marginLeft: 4 }}>{suffix}</span>}
                    </div>
                    {subtitle && <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>{subtitle}</div>}
                </div>
            </div>
        </Card>
    );

    const filteredBeneficiaries = getFilteredBeneficiaries();
    const totalAll = stats.totalAffiliates + stats.totalAmbassadors;

    return (
        <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24,
                gap: 12
            }}>
                <div>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                        Gestion des Commissions
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Gérez les commissions des affiliés et ambassadeurs
                    </Text>
                </div>
                <Space>
                    {/* Bouton commenté temporairement
                    <Button
                        type="primary"
                        icon={<SyncOutlined spin={migrationLoading} />}
                        onClick={handleMigration}
                        loading={migrationLoading}
                        style={{ borderRadius: 8, background: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Générer commissions manquantes
                    </Button>
                    */}
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetchCommissions()}
                        style={{ borderRadius: 8 }}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Stats Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={8} lg={5}>
                    <StatCard
                        icon={<AppstoreOutlined style={{ fontSize: 20, color: '#595959' }} />}
                        iconBg="#f5f5f5"
                        title="Total bénéficiaires"
                        value={totalAll}
                        subtitle={`${stats.totalAffiliates} affiliés, ${stats.totalAmbassadors} ambassadeurs`}
                    />
                </Col>
                <Col xs={12} sm={8} lg={5}>
                    <StatCard
                        icon={<TeamOutlined style={{ fontSize: 20, color: '#1890ff' }} />}
                        iconBg="#e6f7ff"
                        title="Affiliés"
                        value={stats.totalPendingAffiliate}
                        suffix="F"
                        subtitle={`${stats.pendingAffiliate} commissions en attente`}
                        onClick={() => handleTabChange('affiliate')}
                    />
                </Col>
                <Col xs={12} sm={8} lg={5}>
                    <StatCard
                        icon={<CrownOutlined style={{ fontSize: 20, color: '#722ed1' }} />}
                        iconBg="#f9f0ff"
                        title="Ambassadeurs"
                        value={stats.totalPendingAmbassador}
                        suffix="F"
                        subtitle={`${stats.pendingAmbassador} commissions en attente`}
                        onClick={() => handleTabChange('ambassador')}
                    />
                </Col>
                <Col xs={24} sm={12} lg={9}>
                    <Card
                        style={{
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #ff7a45 0%, #fa541c 100%)',
                            border: 'none',
                            height: '100%'
                        }}
                        bodyStyle={{ padding: 16 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{
                                width: 44,
                                height: 44,
                                borderRadius: 10,
                                background: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <WalletOutlined style={{ fontSize: 20, color: '#fff' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>Total à payer</div>
                                <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                                    {stats.totalPending.toLocaleString()}
                                    <span style={{ fontSize: 14, fontWeight: 500, marginLeft: 4 }}>F</span>
                                </div>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Onglets & Table */}
            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
                {/* Onglets */}
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    style={{ padding: '0 20px' }}
                    items={[
                        {
                            key: 'all',
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <AppstoreOutlined />
                                    Tous
                                    <Tag style={{ marginLeft: 4, borderRadius: 10 }}>{totalAll}</Tag>
                                </span>
                            )
                        },
                        {
                            key: 'affiliate',
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <TeamOutlined style={{ color: '#1890ff' }} />
                                    Affiliés
                                    <Tag color="blue" style={{ marginLeft: 4, borderRadius: 10 }}>{stats.totalAffiliates}</Tag>
                                </span>
                            )
                        },
                        {
                            key: 'ambassador',
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <CrownOutlined style={{ color: '#722ed1' }} />
                                    Ambassadeurs
                                    <Tag color="purple" style={{ marginLeft: 4, borderRadius: 10 }}>{stats.totalAmbassadors}</Tag>
                                </span>
                            )
                        }
                    ]}
                />

                {/* Info */}
                <div style={{
                    padding: '12px 20px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {filteredBeneficiaries.length} bénéficiaire{filteredBeneficiaries.length > 1 ? 's' : ''}
                    </Text>
                </div>

                {/* Table */}
                <Table
                    columns={columns}
                    dataSource={filteredBeneficiaries}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        pageSize: 15,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '15', '25', '50'],
                        size: 'small',
                        style: { marginRight: 16 }
                    }}
                    locale={{ emptyText: <Empty description="Aucun bénéficiaire avec des commissions" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default CommissionsPage;
